import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import SEOHead from '@/components/seo/SEOHead';
import { Heart, Milestone, LibraryBig, Layers, LifeBuoy, Shield, Compass, Users } from 'lucide-react';
import AppMockup from '@/components/ui/app-mockup';
import ParallaxSection from '@/components/about/ParallaxSection';
import NewParadigmInfoSection from '@/components/about/NewParadigmInfoSection';
import { MotionDiv, MotionComponent, MotionImg } from '@/utils/optimizedMotion';
import ValueCard from '@/components/about/ValueCard';
import SectionHeading from '@/components/about/SectionHeading';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { Sparkle } from "lucide-react";
import { Footer } from '@/components/layout/Footer';
import { generateSiteStructuredData } from '@/seo/site-structured-data.mjs';
/**
 * AboutUs Component
 * 
 * A modern, cinematic, and SEO-optimized About page for Meduler that aligns with
 * the design philosophy of "Harmony, not Adherence".
 * 
 * Key features:
 * - Follows Meduler Design Framework for styling and color usage
 * - Modern UI with simplified parallax effects for visual interest
 * - Conditional rendering to simplify animations on mobile devices
 * - Mobile-first responsive design with progressive enhancement
 * - Optimized for SEO with structured data
 * - Accessibility compliant with proper semantics and ARIA attributes
 * - Performance-optimized animations using Framer Motion
 * - Offline-capable with static content for PWA support
 */
const AboutUs: React.FC = () => {
  // State to track device type for conditional rendering
  const [isMobile, setIsMobile] = useState(false);
  
  // Check for reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  // Reference for main container for parallax effects
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Setup device detection and motion preference detection
  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check for reduced motion preference
    const checkReducedMotion = () => {
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(query.matches);
    };
    
    checkMobile();
    checkReducedMotion();
    
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  // Create structured data for the about page
  const siteStructuredData = generateSiteStructuredData() as { '@graph': object[] };
  const aboutStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      ...siteStructuredData['@graph'],
      {
        '@type': 'MedicalWebPage',
        'headline': 'About Meduler — Medication Harmony, Not Compliance',
        'about': {
          '@type': 'MedicalEntity',
          'name': 'Medication Adherence and Management'
        },
        'mainContentOfPage': 'Meduler is a medication management app designed to help patients and caregivers build consistent medication habits through reminders, adherence tracking, and an offline-first design that respects user privacy.',
        'lastReviewed': new Date().toISOString().split('T')[0]
      }
    ]
  };

  // Setup scroll parallax effects for the main container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  // Parallax effect values that are conditionally applied
  const backgroundY = useTransform(
    scrollYProgress, 
    [0, 1], 
    ['0%', '20%']
  );
  
  // Only apply complex effects if not mobile and doesn't prefer reduced motion
  const shouldApplyEffects = !isMobile && !prefersReducedMotion;
  
  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-white dark:bg-gray-900 overflow-x-hidden"
    >
      {/* SEO Meta Tags and Structured Data */}
      <SEOHead 
        title="About Meduler — Our Mission & Team"
        description="Learn how Meduler was built to help patients and caregivers manage medications with harmony, not compliance. Meet the team and discover our health-tech philosophy."
        keywords="about meduler, medication app company, medication management mission, health technology team, medication adherence philosophy"
        canonicalUrl="https://www.meduler.com/about"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={aboutStructuredData}
      />
      
      {/* Background Elements - Only visible on desktop */}
      {!isMobile && (
        <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
          <MotionDiv 
            className="absolute inset-0 w-full h-full"
            style={{ y: shouldApplyEffects ? backgroundY : 0 }}
          >
            <div className="absolute -top-1/3 -right-1/3 w-2/3 h-2/3 rounded-full bg-purple-50 dark:bg-purple-900/10 blur-3xl opacity-30"></div>
            <div className="absolute bottom-20 -left-20 w-72 h-72 rounded-full bg-purple-100 dark:bg-purple-900/10 blur-3xl opacity-20"></div>
          </MotionDiv>
        </div>
      )}

      <main className="container mx-auto px-4 sm:px-6 pb-8 md:pb-16 space-y-8 md:space-y-16 relative z-10" data-component-name="AboutUs">
        {/* Hero Section */}
        <ParallaxSection variant="top" className="pt-2 md:pt-4 min-h-[60vh] flex items-center justify-center" reduceMotion={prefersReducedMotion}>
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: isMobile ? 0.6 : 0.8, 
              delay: 0.2,
              ease: "easeOut"
            }}
          >
            <motion.div 
              className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              OUR PHILOSOPHY
            </motion.div>
            
            <motion.h1 
              className="text-3xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <span>Who Is </span>
              <motion.span 
                className="text-purple-600 inline-block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Meduler
              </motion.span>
              <span> and Why Was It Built?</span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              Meduler is a medication management app built for patients and caregivers who want medication to flow naturally into daily life — not feel like a burden. It is free to start, works offline, and never sells your health data.
            </motion.p>
            
            <motion.div 
              className="w-full max-w-xs mx-auto h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent my-8" 
              role="separator"
              aria-hidden="true"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
            />
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="mt-8"
            >
              <a 
                href="#vision" 
                className="inline-flex items-center justify-center gap-2 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
              >
                <span>Discover our approach</span>
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </motion.div>
              </a>
            </motion.div>
          </motion.div>
        </ParallaxSection>

        {/* Vision Section */}
        <section id="vision" className="py-12 md:py-20">
          <ParallaxSection variant="middle" className="max-w-6xl mx-auto" reduceMotion={prefersReducedMotion}>
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
              <motion.div 
                className="order-2 md:order-1 bg-purple-50 dark:bg-gray-800 rounded-xl p-6 shadow-sm overflow-hidden"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-10%" }}
              >
                <motion.div
                  initial={{ scale: isMobile ? 1 : 1.05 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="flex flex-col items-center">
                    <AppMockup 
                      showFutureView={true}
                      className="max-w-[300px] mx-auto" 
                    />
                    <div className="text-center mt-6">
                      <h3 className="text-xl font-bold text-purple-700 mb-2">The Future of Medication Management</h3>
                      <p className="text-gray-600">Our vision of what seamless integration of medications into your daily routine could look like</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
              
              <div className="order-1 md:order-2">
                <SectionHeading
                  eyebrow="OUR VISION"
                  title="Medication as Integration, Not Interruption"
                  centered={false}
                />
                
                <motion.p 
                  className="text-gray-600 dark:text-gray-300 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true, margin: "-10%" }}
                >
                  We envision a world where medication integrates seamlessly into daily life rather than disrupting it. A world where people don't just "take meds" but incorporate them as naturally as other health habits.
                </motion.p>
                
                <motion.div 
                  className="space-y-5"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.15, delayChildren: 0.4 }}
                  viewport={{ once: true, margin: "-10%" }}
                >
                  {[
                    "Making medication moments feel like self-care, not self-enforcement",
                    "Connecting medication to benefits that matter to individuals",
                    "Designing technology that enhances human relationships, not replaces them"
                  ].map((text, index) => (
                    <motion.div 
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      viewport={{ once: true }}
                    >
                      <div className="h-6 w-6 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center" aria-hidden="true">
                        <motion.div 
                          className="h-3 w-3 rounded-full bg-purple-500 dark:bg-purple-400"
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.2 + (index * 0.1) }}
                          viewport={{ once: true }}
                        />
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{text}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </ParallaxSection>
        </section>

        {/* New Paradigm Section */}
        <section id="paradigm" className="py-16 md:py-24">
          <NewParadigmInfoSection />
        </section>

        {/* Target Users Section */}
        <section id="users" className="py-16 md:py-24">
          <ParallaxSection variant="top" className="mx-auto" reduceMotion={prefersReducedMotion}>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <SectionHeading
                eyebrow="WHO WE SERVE"
                title="Designed for Diverse Medication Needs"
                description="Meduler is designed to support everyone on their medication journey, with special consideration for their unique needs and challenges."
                centered={true}
              />
            </div>
            
            <motion.div 
              className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto px-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-10%" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: isMobile ? 0.2 : 0.3,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {[
                {
                  icon: Users,
                  title: "Primary Users",
                  description: "Adults managing ongoing medication regimens for chronic conditions who seek a more harmonious way to integrate treatments into their lives."
                },
                {
                  icon: Heart,
                  title: "Caregivers",
                  description: "Supportive family members and caregivers who help loved ones manage their medications and want tools that respect agency while providing assistance."
                },
                {
                  icon: LifeBuoy,
                  title: "Healthcare Providers",
                  description: "Medical professionals seeking better integration support for patients, with tools that bridge the gap between clinical recommendations and real-life implementation."
                }
              ].map((card, index) => {
                const Icon = card.icon;
                return (
                  <ValueCard
                    key={index}
                    icon={Icon}
                    title={card.title}
                    description={card.description}
                    index={index}
                  />
                );
              })}
            </motion.div>
          </ParallaxSection>
        </section>

        {/* Values Section */}
        <section id="values" className="py-16 md:py-24 overflow-hidden">
          <ParallaxSection variant="middle" className="max-w-6xl mx-auto" reduceMotion={prefersReducedMotion}>
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm py-12 px-4 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-5%" }}
            >
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <SectionHeading
                  eyebrow="OUR VALUES"
                  title="Built on Foundations That Matter"
                  description="Our core values guide everything we do, from product design to partnerships."
                  centered={true}
                />
              </div>
              
              <motion.div 
                className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                initial="hidden"
                whileInView="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: isMobile ? 0.15 : 0.2,
                      delayChildren: 0.3
                    }
                  }
                }}
                viewport={{ once: true, margin: "-10%" }}
              >
                {[
                  {
                    icon: LibraryBig,
                    title: "Integration",
                    description: "We recognize that medication needs to fit within complex lives, not the other way around."
                  },
                  {
                    icon: Compass,
                    title: "Discovery",
                    description: "We support the journey of finding personal patterns that work, embracing exploration over prescription."
                  },
                  {
                    icon: Layers,
                    title: "Empowerment",
                    description: "We design systems that enhance human agency in health management rather than replace it."
                  },
                  {
                    icon: Shield,
                    title: "Recognition",
                    description: "We acknowledge the complexity of lived experience and the diversity of valid approaches to medication integration."
                  }
                ].map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <motion.div 
                      key={index}
                      className="bg-purple-50 dark:bg-gray-700 rounded-xl p-6 md:p-8 shadow-sm h-full flex flex-col"
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { 
                          opacity: 1, 
                          y: 0,
                          transition: { 
                            duration: 0.6, 
                            ease: "easeOut" 
                          }
                        }
                      }}
                      whileHover={isMobile ? {} : { y: -5, transition: { duration: 0.2 } }}
                    >
                      <motion.div 
                        className="bg-purple-100 dark:bg-purple-800 rounded-full w-12 h-12 flex items-center justify-center mb-4" 
                        aria-hidden="true"
                        initial={{ scale: 0.8, opacity: 0 }}
                        whileInView={{ 
                          scale: 1, 
                          opacity: 1,
                          transition: { 
                            duration: 0.5, 
                            delay: 0.2 + (index * 0.1) 
                          }
                        }}
                        viewport={{ once: true }}
                      >
                        <Icon className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                      </motion.div>
                      
                      <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{value.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>
          </ParallaxSection>
        </section>

        {/* Plus Tier Section */}
        <section id="plus" className="py-16 md:py-24 overflow-hidden">
          <ParallaxSection variant="bottom" className="max-w-6xl mx-auto px-4" reduceMotion={prefersReducedMotion}>
            <motion.div 
              className="bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900 dark:to-gray-800 rounded-xl overflow-hidden relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-5%" }}
            >
              {/* Background abstract shapes - Only visible on non-mobile */}
              {!isMobile && (
                <>
                  <motion.div 
                    className="absolute top-0 right-0 w-1/3 h-full opacity-20 dark:opacity-10" 
                    aria-hidden="true"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 0.2, scale: 1 }}
                    transition={{ duration: 1.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="absolute right-0 top-0 w-64 h-64 rounded-full bg-purple-300 dark:bg-purple-700 blur-3xl transform -translate-y-1/2"></div>
                    <div className="absolute right-20 bottom-0 w-32 h-32 rounded-full bg-purple-400 dark:bg-purple-600 blur-2xl transform translate-y-1/3"></div>
                  </motion.div>
                </>
              )}
              
              <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 relative z-10 text-center">
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 text-sm font-medium mb-4">
                    MEDULER PLUS <Sparkle className="ml-1 h-4 w-4 pointer-events-none shrink-0" />
                  </div>
                </motion.div>
                
                <motion.h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-gray-900 dark:text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  Enhanced Integration for Those Who Need More
                </motion.h2>
                
                <motion.p 
                  className="text-lg mb-10 text-gray-700 dark:text-gray-200 max-w-2xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  Our Plus tier offers additional features for those with complex medication regimens or who desire deeper insights into their medication patterns.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <motion.a 
                    href="/pricing" 
                    className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-all"
                    aria-label="Learn more about Meduler Plus pricing and features"
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 15px -3px rgba(124, 58, 237, 0.3), 0 4px 6px -2px rgba(124, 58, 237, 0.2)"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="flex items-center">Learn More About Plus <Sparkle className="ml-1 h-4 w-4 pointer-events-none shrink-0" /></span>
                  </motion.a>
                </motion.div>
              </div>
            </motion.div>
          </ParallaxSection>
        </section>
        
        {/* Product stewardship and accountability */}
        <section id="team" className="py-12 md:py-20">
          <ParallaxSection variant="middle" className="max-w-4xl mx-auto" reduceMotion={prefersReducedMotion}>
            <div className="text-center mb-10">
              <SectionHeading
                eyebrow="HOW WE BUILD"
                title="Product Stewardship"
                description="Meduler is developed around privacy, accessibility, and honest boundaries for a personal medication organization tool."
                centered={true}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {[
                {
                  initials: "PR",
                  name: "Privacy",
                  role: "Minimize unnecessary data",
                  bio: "Core medication organization is designed to work locally, and sharing should remain under the user's control. Public support channels ask users not to send sensitive health details."
                },
                {
                  initials: "AC",
                  name: "Accessibility",
                  role: "Support different routines",
                  bio: "The product is built for varied devices, connectivity, and caregiving situations, with clear navigation and offline-first access to core organization features."
                },
                {
                  initials: "HB",
                  name: "Honest boundaries",
                  role: "Organization, not medical advice",
                  bio: "Meduler does not claim to diagnose, prescribe, verify treatment, or replace a doctor or pharmacist. Product pages state those limitations wherever they are relevant."
                }
              ].map((member, i) => (
                <motion.div
                  key={i}
                  className="bg-purple-50 dark:bg-gray-800 rounded-xl p-6 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 rounded-full bg-purple-200 dark:bg-purple-700 flex items-center justify-center mb-4">
                    <span className="text-purple-700 dark:text-purple-200 font-bold">{member.initials}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-1">{member.name}</h3>
                  <p className="text-sm text-purple-600 dark:text-purple-400 mb-3">{member.role}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{member.bio}</p>
                </motion.div>
              ))}
            </div>

            {/* Medical disclaimer — YMYL E-E-A-T requirement */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-6">
              <h3 className="font-bold text-amber-800 dark:text-amber-300 mb-2">Medical Information Disclaimer</h3>
              <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
                Meduler is a medication scheduling and adherence tool, not a medical device or clinical decision support system. The information provided through Meduler does not constitute medical advice. Always consult a qualified healthcare provider — including your prescriber and pharmacist — regarding your medications, dosages, and any potential drug interactions. Meduler does not check for drug interactions or replace professional medical guidance.
              </p>
            </div>
          </ParallaxSection>
        </section>

        {/* Limitations section — GEO template: honest limitations */}
        <section id="limitations" className="py-10 md:py-12 bg-gray-50 dark:bg-gray-800/50 border-y border-gray-100 dark:border-gray-700">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">When Meduler isn't the right fit</h2>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400 text-sm list-disc list-inside">
              <li>Meduler does not check for drug-drug or drug-food interactions — always consult your pharmacist.</li>
              <li>Meduler is not an electronic health record and does not connect to hospital or pharmacy systems.</li>
              <li>If you need clinical-grade medication management with prescriber integration, a pharmacy management platform may be more appropriate.</li>
              <li>Meduler is not a substitute for professional medical advice, diagnosis, or treatment.</li>
            </ul>
          </div>
        </section>

        {/* Use shared Footer component */}
        <Footer />
      </main>
    </div>
  );
};

export default AboutUs;
